// mds-dojo — dijit/a11y — FAITHFUL port of references/dijit/a11y.js. Accessibility
// utilities: tab-stop detection + finding first/last tab-navigable descendants
// (used by Dialog focus-trap, Menu, _HasDropDown, _KeyNavContainer). IE scopeName
// branch dropped (evergreen); has("extend-dojo") mixin-to-namespace dropped.
import dom from '../dojo/dom';
import domAttr from '../dojo/dom-attr';
import domStyle from '../dojo/dom-style';

const a11y: any = {
  _isElementShown: function (elem: any) {
    const s = domStyle.get(elem);
    return (s.visibility != 'hidden')
      && (s.visibility != 'collapsed')
      && (s.display != 'none')
      && (domAttr.get(elem, 'type') != 'hidden');
  },

  hasDefaultTabStop: function (elem: any) {
    // Tab-navigable even without an explicit tabIndex?
    switch (elem.nodeName.toLowerCase()) {
      case 'a':
        return domAttr.has(elem, 'href');
      case 'area':
      case 'button':
      case 'input':
      case 'object':
      case 'select':
      case 'textarea':
        return true;
      case 'iframe':
        var body;
        try {
          const contentDocument = elem.contentDocument;
          if ('designMode' in contentDocument && contentDocument.designMode == 'on') {
            return true;
          }
          body = contentDocument.body;
        } catch (e1) {
          try {
            body = elem.contentWindow.document.body;
          } catch (e2) {
            return false;
          }
        }
        return body && (body.contentEditable == 'true' ||
          (body.firstChild && body.firstChild.contentEditable == 'true'));
      default:
        return elem.contentEditable == 'true';
    }
  },

  effectiveTabIndex: function (elem: any) {
    // Effective tabIndex (number), or undefined if not focusable.
    if (domAttr.get(elem, 'disabled')) {
      return undefined;
    } else if (domAttr.has(elem, 'tabIndex')) {
      return +domAttr.get(elem, 'tabIndex');
    } else {
      return a11y.hasDefaultTabStop(elem) ? 0 : undefined;
    }
  },

  isTabNavigable: function (elem: any) {
    return a11y.effectiveTabIndex(elem) >= 0;
  },

  isFocusable: function (elem: any) {
    return a11y.effectiveTabIndex(elem) >= -1;
  },

  _getTabNavigable: function (root: any) {
    // Find first/last tab-navigable (tabIndex 0) + lowest/highest positive-tabIndex
    // descendants of root.
    let first: any, last: any, lowest: any, lowestTabindex: any, highest: any, highestTabindex: any;
    const radioSelected: any = {};

    function radioName(node: any) {
      return node && node.tagName.toLowerCase() == 'input' &&
        node.type && node.type.toLowerCase() == 'radio' &&
        node.name && node.name.toLowerCase();
    }

    const shown = a11y._isElementShown, effectiveTabIndex = a11y.effectiveTabIndex;
    const walkTree = function (parent: any) {
      for (let child = parent.firstChild; child; child = child.nextSibling) {
        if (child.nodeType != 1 || !shown(child)) {
          continue;
        }

        const tabindex = effectiveTabIndex(child);
        if (tabindex >= 0) {
          if (tabindex == 0) {
            if (!first) { first = child; }
            last = child;
          } else if (tabindex > 0) {
            if (!lowest || tabindex < lowestTabindex) {
              lowestTabindex = tabindex;
              lowest = child;
            }
            if (!highest || tabindex >= highestTabindex) {
              highestTabindex = tabindex;
              highest = child;
            }
          }
          const rn = radioName(child);
          if (domAttr.get(child, 'checked') && rn) {
            radioSelected[rn] = child;
          }
        }
        if (child.nodeName.toUpperCase() != 'SELECT') {
          walkTree(child);
        }
      }
    };
    if (shown(root)) {
      walkTree(root);
    }
    function rs(node: any) {
      return radioSelected[radioName(node)] || node;
    }

    return { first: rs(first), last: rs(last), lowest: rs(lowest), highest: rs(highest) };
  },

  getFirstInTabbingOrder: function (root: any, doc?: any) {
    const elems = a11y._getTabNavigable(dom.byId(root, doc));
    return elems.lowest ? elems.lowest : elems.first;
  },

  getLastInTabbingOrder: function (root: any, doc?: any) {
    const elems = a11y._getTabNavigable(dom.byId(root, doc));
    return elems.last ? elems.last : elems.highest;
  },
};

export default a11y;
